<template>
  <component
    :is="props.as"
    ref="rootRef"
    data-slot="scroll-shadow"
    data-component="ScrollShadow"
    :data-top-scroll="topScrollAttr"
    :data-bottom-scroll="bottomScrollAttr"
    :data-top-bottom-scroll="topBottomScrollAttr"
    :data-left-scroll="leftScrollAttr"
    :data-right-scroll="rightScrollAttr"
    :data-left-right-scroll="leftRightScrollAttr"
    :class="
      props.orientation === 'vertical' ? 'overflow-y-auto' : 'overflow-x-auto'
    "
    :style="`--scroll-shadow-size: ${shadowSize};`"
  >
    <slot />
  </component>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, toRefs } from 'vue';

/**
 * @typedef {object} ScrollShadowProps
 * @property {'vertical' | 'horizontal'} [orientation='vertical'] - The orientation of the scroll shadow.
 * @property {string} [shadowSize='36px'] - The size of the scroll shadow.
 */

/**
 * @type {ScrollShadowProps}
 */
const props = defineProps({
  as: { type: String, default: 'div' },
  orientation: { type: String, default: 'vertical' },
  shadowSize: { type: String, default: '36px' },
});

function useScroll(elementRef) {
  const arrivedState = reactive({
    left: true,
    right: false,
    top: true,
    bottom: false,
  });

  let resizeObserver = null;

  const updateScrollState = () => {
    const el = elementRef.value;
    if (!el) return;

    const threshold = 1; // Threshold for floating point comparisons

    // Vertical scroll
    if (el.scrollHeight <= el.clientHeight) {
      arrivedState.top = true;
      arrivedState.bottom = true;
    } else {
      arrivedState.top = el.scrollTop <= threshold;
      arrivedState.bottom =
        el.scrollHeight - el.clientHeight - el.scrollTop <= threshold;
    }

    // Horizontal scroll
    if (el.scrollWidth <= el.clientWidth) {
      arrivedState.left = true;
      arrivedState.right = true;
    } else {
      arrivedState.left = el.scrollLeft <= threshold;
      arrivedState.right =
        el.scrollWidth - el.clientWidth - el.scrollLeft <= threshold;
    }
  };

  onMounted(() => {
    const el = elementRef.value;
    if (el) {
      updateScrollState(); // Initial check
      el.addEventListener('scroll', updateScrollState, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(el);
      } else {
        // Fallback for environments without ResizeObserver
        window.addEventListener('resize', updateScrollState, { passive: true });
      }
    }
  });

  onUnmounted(() => {
    const el = elementRef.value;
    if (el) {
      el.removeEventListener('scroll', updateScrollState);
    }
    if (resizeObserver) {
      resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', updateScrollState);
    }
  });

  return {
    arrivedState,
    updateScrollState,
  };
}

const rootRef = ref(null);

const { arrivedState, updateScrollState } = useScroll(rootRef);

const { left, right, top, bottom } = toRefs(arrivedState);

const isVerticallyScrollable = computed(() => !(top.value && bottom.value));
const isHorizontallyScrollable = computed(() => !(left.value && right.value));

const topScrollAttr = computed(() => {
  if (props.orientation !== 'vertical' || !isVerticallyScrollable.value) {
    return undefined;
  }
  return bottom.value || undefined;
});

const bottomScrollAttr = computed(() => {
  if (props.orientation !== 'vertical' || !isVerticallyScrollable.value) {
    return undefined;
  }
  return top.value || undefined;
});

const topBottomScrollAttr = computed(() => {
  if (props.orientation !== 'vertical' || !isVerticallyScrollable.value) {
    return undefined;
  }
  return (!top.value && !bottom.value) || undefined;
});

const leftScrollAttr = computed(() => {
  if (props.orientation !== 'horizontal' || !isHorizontallyScrollable.value) {
    return undefined;
  }
  return right.value || undefined;
});

const rightScrollAttr = computed(() => {
  if (props.orientation !== 'horizontal' || !isHorizontallyScrollable.value) {
    return undefined;
  }
  return left.value || undefined;
});

const leftRightScrollAttr = computed(() => {
  if (props.orientation !== 'horizontal' || !isHorizontallyScrollable.value) {
    return undefined;
  }
  return (!left.value && !right.value) || undefined;
});

defineExpose({
  updateScrollState,
});
</script>

<style>
[data-component='ScrollShadow'][data-top-scroll='true'] {
  mask-image: linear-gradient(
    0deg,
    black calc(100% - var(--scroll-shadow-size)),
    transparent
  );
  -webkit-mask-image: linear-gradient(
    0deg,
    black calc(100% - var(--scroll-shadow-size)),
    transparent
  );
}
[data-component='ScrollShadow'][data-bottom-scroll='true'] {
  mask-image: linear-gradient(
    180deg,
    black calc(100% - var(--scroll-shadow-size)),
    transparent
  );
  -webkit-mask-image: linear-gradient(
    180deg,
    black calc(100% - var(--scroll-shadow-size)),
    transparent
  );
}
[data-component='ScrollShadow'][data-top-bottom-scroll='true'] {
  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black var(--scroll-shadow-size),
    black calc(100% - var(--scroll-shadow-size)),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black var(--scroll-shadow-size),
    black calc(100% - var(--scroll-shadow-size)),
    transparent 100%
  );
}

[data-component='ScrollShadow'][data-left-scroll='true'] {
  mask-image: linear-gradient(
    270deg,
    black calc(100% - var(--scroll-shadow-size)),
    transparent
  );
  -webkit-mask-image: linear-gradient(
    270deg,
    black calc(100% - var(--scroll-shadow-size)),
    transparent
  );
}
[data-component='ScrollShadow'][data-right-scroll='true'] {
  mask-image: linear-gradient(
    90deg,
    black calc(100% - var(--scroll-shadow-size)),
    transparent
  );
  -webkit-mask-image: linear-gradient(
    90deg,
    black calc(100% - var(--scroll-shadow-size)),
    transparent
  );
}
[data-component='ScrollShadow'][data-left-right-scroll='true'] {
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black var(--scroll-shadow-size),
    black calc(100% - var(--scroll-shadow-size)),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black var(--scroll-shadow-size),
    black calc(100% - var(--scroll-shadow-size)),
    transparent 100%
  );
}
</style>
